import clsx from 'clsx'
import { store } from '../../../stores/store'
import { GetTooltipsDocument } from '../../../lib/graphql/generated/graphql'
import type { ParentsInfoBannerBlockId, ParentsInfoBannerRenderId } from './parentsInfoBannerTypes'

const ICON_SIZE = 196
const LEFT_GAP = 48
const TEXT_GAP = 32
const HEADLINE_FONT_SIZE = 56
const BODY_FONT_SIZE = 56
const BODY_MAX_WIDTH = 1000

const POWERUP_ICON_BY_TYPE: Record<ParentsInfoBannerBlockId | 'loupe', string> = {
    coin: '/images/parentsBannerIcons/Property 1=Coin.png',
    heart: '/images/parentsBannerIcons/Property 1=Heart.png',
    shield: '/images/parentsBannerIcons/Property 1=Shield.png',
    battery: '/images/parentsBannerIcons/Property 1=Battery.png',
    plane: '/images/parentsBannerIcons/Property 1=Star.png',
    loupe: '/images/parentsBannerIcons/Property 1=Loupe.png',
    endgame: '/images/parentsBannerIcons/Property 1=Endgame.png',
    gameOver: '/images/parentsBannerIcons/Property 1=GameOver.png',
    level1: '/images/parentsBannerIcons/Property 1=Level 1.png',
    level2: '/images/parentsBannerIcons/Property 1=Level 2.png',
    level3: '/images/parentsBannerIcons/Property 1=Level 3.png',
    level4: '/images/parentsBannerIcons/Property 1=Level 4.png',
}

type ParentsInfoBannerBlockContent = {
    subline?: string
    body: string
    icon?: string
    iconEmoji?: string
}

function getBlockVariantContent(type: ParentsInfoBannerBlockId): ParentsInfoBannerBlockContent {
    const tooltip = store.getCachedQuery(GetTooltipsDocument)?.tooltip

    switch (type) {
        case 'coin':
            return {
                subline: tooltip?.CoinsTitle ?? 'Vorsorge-Münzen',
                body: 'Vorsorge-Münzen =  Zukunftsvorsorge. Wer früh startet, kann dank Zinseszins langfristig Vermögen aufbauen.',
                icon: POWERUP_ICON_BY_TYPE.coin,
            }
        case 'heart':
            return {
                subline: 'Herz',
                body: 'Herz = Krankenschutz. Hilft für bestmögliche Versorgung und Unterstützung im Ernstfall.',
                icon: POWERUP_ICON_BY_TYPE.heart,
            }
        case 'shield':
            return {
                subline: tooltip?.ShieldTitle ?? 'Schutzschild',
                body: 'Schutzschild = Grundfähigkeitsschutz. Sichert die wichtigen Fähigkeiten Ihres Kindes finanziell ab. ',
                icon: POWERUP_ICON_BY_TYPE.shield,
            }
        case 'battery':
            return {
                subline: tooltip?.SnapTitle ?? 'Unfall-Blitz',
                body: 'Unfall-Blitz =  Unfallabsicherung. Leistet nach unerwarteten Ereignissen schnelle finanzielle Unterstützung.',
                icon: POWERUP_ICON_BY_TYPE.battery,
            }
        case 'plane':
            return {
                subline: tooltip?.BoostTitle ?? 'Star-Boost',
                body: 'Boost = Berufsunfähigkeitsschutz. Hilft, wenn Ihr Kind später den Beruf krankheits- oder unfallbedingt nicht mehr ausüben kann.',
                icon: POWERUP_ICON_BY_TYPE.plane,
            }
        case 'gameOver':
            return {
                subline: 'Es kann viel passieren … ',
                body: 'Im Leben läuft nicht immer alles nach Plan. Aber mit der richtigen Vorsorge geht’s leichter.',
                icon: POWERUP_ICON_BY_TYPE.gameOver,
            }
        case 'endgame':
            return {
                subline: 'Das Leben durchgespielt! ',
                body: 'Ihr Kind kann bis zu 4 limitierte Karten sammeln. Fragen Sie einfach unser Team hier. ',
                icon: POWERUP_ICON_BY_TYPE.endgame,
            }
        case 'level1':
            return {
                subline: 'Die Kleinkind-Phase. ',
                body: 'Große Abenteuer beginnen klein. Hier legen Sie den Grundstein für alles was kommt.',
                icon: POWERUP_ICON_BY_TYPE.level1,
            }
        case 'level2':
            return {
                subline: 'Die Schul-Phase. ',
                body: 'Lernen, wachsen, entdecken. Die richtige Vorsorge begleitet Kinder in die Selbstständigkeit.',
                icon: POWERUP_ICON_BY_TYPE.level2,
            }
        case 'level3':
            return {
                subline: 'Die Berufs-Phase. ',
                body: 'Ihr Kind ist jetzt erwachsen und für sich selbst verantwortlich. Hier wird die Vorsorge relevant.',
                icon: POWERUP_ICON_BY_TYPE.level3,
            }
        case 'level4':
            return {
                subline: 'Die Zukunft. ',
                body: 'Hier zahlen sich alle guten Entscheidungen von heute voll aus! ',
                icon: POWERUP_ICON_BY_TYPE.level4,
            }
        default:
            return {
                subline: 'Hier gucken die Eltern: ',
                body: 'Gleich läuft Ihr Kind durch’s ganze Leben:Kita, Schule, Beruf – bis in die Zukunft. Neugierig?',
                icon: POWERUP_ICON_BY_TYPE.loupe,
            }
    }
}

function getBlockContent(
    blockId: ParentsInfoBannerRenderId,
): ParentsInfoBannerBlockContent {
    if (blockId === 'placeholder') {
        return {
            body: 'Erfahren Sie, was die Power-Ups fürs echte Leben bedeuten und wie sie Ihrem Kind helfen.',
            icon: POWERUP_ICON_BY_TYPE.loupe,
        }
    }

    return getBlockVariantContent(blockId)
}

type ParentsInfoBannerBlockProps = {
    blockId: ParentsInfoBannerRenderId
    className?: string
}

export default function ParentsInfoBannerBlock({
    blockId,
    className,
}: ParentsInfoBannerBlockProps) {
    const { subline, body, icon, iconEmoji } = getBlockContent(blockId)
    const bodyMaxWidth = blockId === 'placeholder' ? BODY_MAX_WIDTH : undefined

    return (
        <div
            className={clsx(
                'flex flex-col justify-center items-start w-full h-full overflow-hidden',
                className,
            )}
            style={{ gap: LEFT_GAP }}
        >
            <div className='flex flex-col w-full min-w-0' style={{ gap: TEXT_GAP }}>
                <div className='flex items-center'>
                    <div
                        className='flex justify-center items-center shrink-0'
                        style={{ width: ICON_SIZE, height: ICON_SIZE }}
                    >
                        <img
                            src={icon}
                            alt=''
                            aria-hidden
                            className='size-full object-contain pointer-events-none'
                        />
                    </div>

                    <div className='flex justify-center items-center px-16 rounded-r-full h-34 bg-text-chip'>
                        <p
                            className='text-white-stroke-even-darker font-nunito font-black italic leading-[100%]'
                            style={{ fontSize: HEADLINE_FONT_SIZE }}
                        >
                            {blockId === 'placeholder' ? 'Hier gucken die Eltern:' : subline}
                        </p>
                    </div>
                </div>
                <p
                    className='text-white-stroke-darker w-full font-nunito font-bold leading-[135%]'
                    style={{
                        fontSize: BODY_FONT_SIZE,
                        maxWidth: bodyMaxWidth,
                    }}
                >
                    {body}
                </p>
            </div>
        </div>
    )
}
